/* Americanflat main product section — variant picker, price/availability
   updates, size visual depiction, quantity stepper, thumbnail gallery.

   Wires up every element with [data-af-product-root]. Reads:
     - data-af-atc-label  (text to show on the Add-to-cart button when available)
   The product JSON is read from a sibling/descendant <script
   data-af-product-json> tag. */
(function () {
  function moneyFmt(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* Bulk-ladder money — MUST match Shopify, which is the authority here.
     The Function returns a PERCENTAGE (`value.percentage`), so Shopify's discount engine
     does the allocation: it FLOORS the per-UNIT discount to whole cents, then multiplies by
     quantity. It does not round the line total. For $18.99 x 3 at 5%:
       Shopify : 5697 - floor(1899 * 5 / 100) * 3 = 5697 - 282 = 5415  ($54.15)
       naive   : round(5697 * 0.95)               =              5412  ($54.12)
     The theme used the naive form, so every CTA under-quoted the cart by a few cents
     (AF-01: +$0.03 on 3 x $18.99, +$0.07 on 8 x $22.99). Because the discount is a
     percentage, this rounding lives inside Shopify and cannot be changed from the Function
     without switching to fixedAmount — so the display has to follow it, not the reverse.
     Side effect worth knowing: flooring makes the effective rate slightly under the
     advertised one (4.950% on that first line, 7.960% on the second). */
  function bulkUnitDiscount(unitCents, pct) {
    return Math.floor(unitCents * pct / 100);
  }
  function bulkNetUnit(unitCents, pct) {
    return unitCents - bulkUnitDiscount(unitCents, pct);
  }
  function bulkLineTotal(unitCents, qty, pct) {
    return bulkNetUnit(unitCents, pct) * qty;
  }

  function init(root) {
    const productJsonEl = document.querySelector('[data-af-product-json][data-section-id="' + root.dataset.sectionId + '"]')
      || root.querySelector('[data-af-product-json]');
    if (!productJsonEl) return;

    let product;
    try {
      product = JSON.parse(productJsonEl.textContent);
    } catch (e) {
      console.error('[af-main-product] Failed to parse product JSON', e);
      return;
    }

    const variantIdInput = root.querySelector('[data-af-variant-id]');

    // Oversized items (AF Shipping Functions app). The section renders the flagged
    // variant ids and which parts are switched on; this keeps the note and the
    // free-shipping callout in step with the selected variant.
    const osCfg = root.querySelector('[data-af-os]');
    const osNote = root.querySelector('[data-af-os-note-el]');
    const osLabel = root.querySelector('[data-af-fs-label]');
    function updateOversize(variant) {
      if (!osCfg) return;
      const on = !!variant && (osCfg.getAttribute('data-af-os-ids') || '').indexOf(',' + variant.id + ',') !== -1;
      if (osNote) osNote.hidden = !on;
      if (osLabel) osLabel.textContent = on ? 'Oversized item' : osLabel.getAttribute('data-af-fs-text');
    }
    const priceEl = root.querySelector('[data-af-price]');
    const atcBtn = root.querySelector('[data-af-atc]');
    const atcLabel = root.querySelector('[data-af-atc-label]');
    const atcLabelText = root.dataset.afAtcLabel || 'Add to cart';
    // Bundle that no single warehouse can build whole (afl.single_site_qty == 0).
    // Shopify still reports the variant available — it pools stock across locations —
    // so availability has to be ANDed with this flag everywhere, or the first render
    // silently re-enables a button Liquid deliberately disabled.
    const forceSoldOut = root.dataset.afForceSoldout === '1';
    // Mobile sticky bar mirrors of price + add button (kept in sync on variant change).
    const stickyPriceEl = root.querySelector('[data-af-sticky-price]');
    const stickyBtn = root.querySelector('[data-af-sticky-add]');
    const stickyLabel = root.querySelector('[data-af-sticky-label]');
    // Wholesale threshold — keep in step with the discounts, the theme cart gate,
    // and the checkout-validation Function. Above this, route to the quote flow.
    const BULK_MAX = 50;
    let overBulk = false;
    let renderedVariant = null;

    // AJAX add-to-cart → open the cart drawer (when one is on the page).
    // Falls back to a normal form submit (→ /cart) if there's no drawer.
    const productForm = atcBtn && atcBtn.closest('form');
    if (productForm) {
      productForm.addEventListener('submit', async (e) => {
        // Browser history can restore radio checks after the initial render.
        // Use the variant we displayed (and persisted in the URL), not those
        // independently restored form controls, including for native submit.
        restoreVariantSelection(false);
        // Bulky order → wholesale quote instead of a blocked add-to-cart.
        if (overBulk) { e.preventDefault(); window.location.href = '/pages/wholesale'; return; }
        const drawer = document.querySelector('cart-drawer');
        if (!drawer || typeof drawer.renderContents !== 'function') return; // native submit
        e.preventDefault();
        if (atcBtn.hasAttribute('disabled')) return;

        const original = atcLabel ? atcLabel.textContent : '';
        let confirmed = false;
        if (atcLabel) atcLabel.textContent = 'Adding…';
        atcBtn.setAttribute('aria-busy', 'true');
        try {
          const fd = new FormData(productForm);
          // Re-render just the cart-drawer section; the count badge is synced
          // separately via /cart.js below.
          fd.append('sections', 'cart-drawer');
          fd.append('sections_url', window.location.pathname);
          const res = await fetch('/cart/add.js', {
            method: 'POST', headers: { Accept: 'application/javascript' }, body: fd,
          });
          const data = await res.json();
          if (data.status) { // e.g. sold out / not enough stock
            if (atcLabel) atcLabel.textContent = data.message || 'Unavailable';
            setTimeout(() => { if (atcLabel) atcLabel.textContent = original; }, 1500);
            return;
          }
          // Swap in the freshly rendered drawer markup, then open it the SAME
          // way the header cart icon does (a plain drawer.open()). Going through
          // Dawn's renderContents() — which swaps innerHTML and auto-opens on its
          // own setTimeout timing — is unreliable on Safari: it can leave the
          // drawer half-slid and showing stale/empty content. Mirroring the
          // proven cart-icon path fixes that across browsers.
          const sectionHTML = data.sections && data.sections['cart-drawer'];
          const live = document.querySelector('#CartDrawer');
          if (sectionHTML && live) {
            const fresh = new DOMParser()
              .parseFromString(sectionHTML, 'text/html')
              .querySelector('#CartDrawer');
            if (fresh) {
              live.innerHTML = fresh.innerHTML;
              drawer.classList.remove('is-empty');
              const overlay = live.querySelector('#CartDrawer-Overlay');
              if (overlay) overlay.addEventListener('click', () => drawer.close());
            }
          }
          // Force layout so Safari commits the new .drawer__inner's initial
          // (off-screen) transform before the slide-in class is applied.
          const inner = drawer.querySelector('.drawer__inner');
          if (inner) void inner.offsetWidth;
          drawer.open(atcBtn);
          confirmed = true;
          if (atcLabel) atcLabel.textContent = 'Added ✓';
          // Keep the header cart-count badge in sync.
          fetch('/cart.js').then((r) => r.json()).then((c) => {
            document.querySelectorAll('[data-af-cart-count]').forEach((el) => {
              el.textContent = c.item_count;
              el.hidden = c.item_count === 0;
            });
          }).catch(() => {});
        } catch (err) {
          productForm.submit(); // last-resort fallback
        } finally {
          atcBtn.removeAttribute('aria-busy');
          if (atcLabel) setTimeout(() => { atcLabel.textContent = original; }, confirmed ? 1600 : 600);
        }
      });
    }

    function selectedValues() {
      const out = [];
      root.querySelectorAll('input[type="radio"][data-af-option-index]:checked').forEach((r) => {
        out[parseInt(r.dataset.afOptionIndex, 10) - 1] = r.value;
      });
      return out;
    }

    // Normalize option values so a selected radio always resolves to its variant
    // even across formatting differences ('11" x 14"' vs '11x14', ×/x, casing).
    // A silent miss here is what left the default variant id in the form.
    function normOpt(s) {
      return String(s == null ? '' : s).toLowerCase().replace(/[″"']/g, '').replace(/\s+/g, '').replace(/×/g, 'x');
    }
    function findVariant(values) {
      // Single-variant products (no option pickers — e.g. "Default Title") render
      // no radios, so `values` is empty. Resolve to the lone variant instead of
      // failing, otherwise the on-load resolve would disable Add-to-cart.
      if (values.length === 0 && product.variants.length === 1) return product.variants[0];
      return product.variants.find((v) =>
        v.options.every((opt, i) => normOpt(opt) === normOpt(values[i]))
      );
    }

    // Variant picker cascades in OPTION ORDER. For an option value, find the best
    // matching variant while holding the options BEFORE it fixed to the current
    // selection and letting the options AFTER it float; prefer an available variant
    // (then one that also keeps the later current choices). This makes earlier
    // options "sticky": changing a later option (e.g. Pack) never rewrites an
    // earlier, deliberate choice (e.g. Colour), and changing an earlier option
    // cascades forward to re-pick the later ones. Handles both coupled options
    // (Hudson: Mat follows Size) and sparse grids (My Mini: most colours are
    // 1-pack only) without the old "snap to a different colour" surprise.
    // "Sticky" only protects DELIBERATE choices. Options the shopper has
    // actually clicked this session are recorded here — index in `touched`,
    // the chosen VALUE in `preferred`. Anything else is just the page-load
    // default and may be rewritten to reach an AVAILABLE variant (Angela,
    // Aug 2026: picking a size showed "Sold out" because the defaulted Style
    // blocked it, when the other Style was in stock).
    //
    // `preferred` matters when a cascade FORCES a deliberate choice away
    // (18x24 only in Black → colour flips off the chosen Gold): the radios now
    // show Black, but the intent is still Gold — so a later size change scores
    // candidates against Gold and restores it the moment it's available again.
    const touched = new Set();
    const preferred = {};
    function wantValue(i, cur) {
      return preferred[i] != null ? preferred[i] : cur[i];
    }

    function bestVariant(ci, val, cur) {
      const cands = product.variants.filter((v) => {
        if (normOpt(v.options[ci]) !== normOpt(val)) return false;
        for (let i = 0; i < ci; i++) {
          if (normOpt(v.options[i]) !== normOpt(cur[i])) return false;
        }
        return true;
      });
      if (!cands.length) return null;
      function score(v) {
        let s = v.available ? 1000 : 0;
        for (let i = ci + 1; i < v.options.length; i++) {
          if (normOpt(v.options[i]) === normOpt(wantValue(i, cur))) s += 1;
        }
        return s;
      }
      cands.sort((a, b) => score(b) - score(a));
      return cands[0];
    }

    // Relaxed resolution: an AVAILABLE variant carrying `val` at option `ci`,
    // holding only the TOUCHED other options fixed (to their deliberate value)
    // — untouched ones float. Among candidates, prefer the one keeping most of
    // the wanted selection, weighted toward earlier options.
    function relaxedVariant(ci, val, cur) {
      const cands = product.variants.filter((v) => {
        if (!v.available) return false;
        if (normOpt(v.options[ci]) !== normOpt(val)) return false;
        for (let i = 0; i < v.options.length; i++) {
          if (i === ci || !touched.has(i)) continue;
          if (normOpt(v.options[i]) !== normOpt(wantValue(i, cur))) return false;
        }
        return true;
      });
      if (!cands.length) return null;
      const score = (v) => {
        let s = 0;
        for (let i = 0; i < v.options.length; i++) {
          if (normOpt(v.options[i]) === normOpt(wantValue(i, cur))) s += v.options.length - i;
        }
        return s;
      };
      cands.sort((a, b) => score(b) - score(a));
      return cands[0];
    }

    function resolveOnChange(ci, val) {
      const cur = selectedValues();
      cur[ci] = val;
      let target = bestVariant(ci, val, cur);
      // The strict result is dead (no combo, or sold out): before showing
      // "Sold out", let UNTOUCHED options float to an available variant.
      // Deliberate (touched) choices are never rewritten — that guard is the
      // "picked a 2-pack and my colour changed" fix and it stays.
      if (!target || !target.available) {
        target = relaxedVariant(ci, val, cur) || target;
      }
      // Nothing carries this value even relaxed: leave the selection alone
      // (the strike states explain why).
      if (!target) return;
      const t = target.options;
      root.querySelectorAll('input[type="radio"][data-af-option-index]').forEach((r) => {
        const i = parseInt(r.dataset.afOptionIndex, 10) - 1;
        if (normOpt(r.value) === normOpt(t[i])) r.checked = true;
      });
    }

    function updateSizeVisual(values) {
      const visual = root.querySelector('[data-af-size-visual]');
      if (!visual) return;
      const idx = parseInt(visual.dataset.afSizeOption, 10) - 1;
      const value = (values && values[idx]) || '';
      const m = value.match(/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/);
      if (!m) {
        visual.style.display = 'none';
        return;
      }
      visual.style.display = '';
      const w = parseFloat(m[1]);
      const h = parseFloat(m[2]);
      const widthPct = (w * 80) / 84;
      const frame = visual.querySelector('[data-af-size-frame]');
      if (frame) {
        frame.style.width = widthPct + '%';
        frame.style.aspectRatio = w + ' / ' + h;
      }
      const label = visual.querySelector('[data-af-size-label]');
      if (label) label.textContent = value + '"';
    }

    // Resize a Shopify CDN image URL to a target width via the ?width= param.
    function sizedUrl(src, w) {
      if (!src) return src;
      try {
        const u = new URL(src, window.location.href);
        u.searchParams.set('width', String(w));
        return u.toString();
      } catch (e) {
        return src;
      }
    }

    // Swap the main image (and sync the active thumbnail) to the selected
    // variant's featured image. No-op when the variant has no image assigned,
    // so size-only changes that share an image leave the gallery untouched.
    //
    // The hero must ALWAYS be reachable from the thumb strip. The strip is
    // capped at 6 on render, but a variant switch can pull an image from deep
    // in the media list — that image gets a reusable dynamic slot at the front
    // of the strip instead of silently having no thumbnail (which made the
    // hero unrecoverable after one click through the carousel — Angela, Aug 2026).
    function updateVariantMedia(variant) {
      const fi = variant && variant.featured_image;
      if (!fi || !fi.src) return;
      // '#flat' in the image alt marks a lifestyle/scene shot: render
      // full-bleed with no float/shadow treatment, and strip the tag
      // from the visible alt text.
      const fiRawAlt = fi.alt || '';
      const fiIsFlat = fiRawAlt.indexOf('#flat') !== -1;
      const fiCleanAlt = fiRawAlt.replace('#flat', '').trim() || product.title || '';
      const box = root.querySelector('[data-af-main-image]');
      if (box) {
        const img = document.createElement('img');
        img.src = sizedUrl(fi.src, 1200);
        img.alt = fiCleanAlt;
        img.loading = 'eager';
        box.innerHTML = '';
        box.appendChild(img);
        box.classList.toggle('is-primary', !fiIsFlat);
      }
      let matched = null;
      const thumbs = root.querySelectorAll('[data-af-thumb-index]');
      thumbs.forEach((t) => {
        if (fi.id != null && !t.hasAttribute('data-af-thumb-dynamic')
            && t.dataset.afThumbImageId === String(fi.id)) matched = t;
      });
      const strip = root.querySelector('[data-af-thumbs]');
      let dyn = strip && strip.querySelector('[data-af-thumb-dynamic]');
      if (matched) {
        // The image already has a real thumb — use it, park the dynamic slot.
        if (dyn) dyn.hidden = true;
        thumbs.forEach((t) => t.classList.remove('active'));
        matched.classList.add('active');
      } else if (strip) {
        if (!dyn) {
          dyn = document.createElement('button');
          dyn.type = 'button';
          dyn.className = 'af-pdp-thumb';
          dyn.setAttribute('data-af-thumb-dynamic', '');
          dyn.setAttribute('data-af-thumb-index', 'dyn');
          dyn.appendChild(document.createElement('img'));
          strip.prepend(dyn);
        }
        dyn.hidden = false;
        dyn.dataset.afThumbImageId = fi.id != null ? String(fi.id) : '';
        dyn.dataset.afThumbSrc = sizedUrl(fi.src, 1200);
        dyn.dataset.afThumbAlt = fiCleanAlt;
        // Variant images are frame shots unless alt-tagged '#flat' (lifestyle).
        dyn.dataset.afThumbPrimary = fiIsFlat ? 'false' : 'true';
        dyn.setAttribute('aria-label', 'View selected variant image');
        const ti = dyn.querySelector('img');
        ti.src = sizedUrl(fi.src, 160);
        ti.alt = fiCleanAlt;
        ti.loading = 'eager';
        root.querySelectorAll('[data-af-thumb-index]').forEach((t) => t.classList.remove('active'));
        dyn.classList.add('active');
      }
    }

    // REACHABILITY cross-out: strike a value only when CLICKING IT cannot land
    // on a buyable variant — the exact rule resolveOnChange() follows (strict
    // snap-forward, then untouched options float). The old rule judged each
    // value against the EXACT current combo, which on coupled-option products
    // (Hudson: every size has its own mat) struck out essentially every other
    // size — a wall of "sold out" for tiles that worked fine when clicked
    // (Juan, Aug 2026). The strike now means what shoppers think it means:
    // "you can't buy this value right now (given your deliberate choices)".
    // The CHECKED value instead mirrors the Add-to-cart truth, so a genuinely
    // dead current selection still reads as sold out. Values dead on EVERY
    // variant are removed earlier by hideDeadOptionValues().
    function updateOptionStates(values) {
      const current = findVariant(values);
      root.querySelectorAll('input[type="radio"][data-af-option-index]').forEach((r) => {
        const el = r.closest('.af-finish-opt, .af-size-tile');
        if (!el) return;
        const ci = parseInt(r.dataset.afOptionIndex, 10) - 1;
        let dead;
        if (r.checked) {
          dead = !current || !current.available;
        } else {
          const b = bestVariant(ci, r.value, values);
          dead = !(b && b.available) && !relaxedVariant(ci, r.value, values);
        }
        el.classList.toggle('is-soldout', dead);
        el.classList.remove('is-unavailable');
        // Combos that don't exist must not be selectable (Juan, 2026-07-17).
        // Disable the radio exactly when resolveOnChange would dead-end: no
        // variant carries this value with the EARLIER options held fixed
        // (later options float), AND the relaxed pass (untouched options
        // float too) can't reach it either. A struck-but-reachable tile
        // (e.g. a size whose mat will snap forward) stays clickable; the
        // checked radio is never disabled so the current selection always
        // has an escape hatch.
        r.disabled = !r.checked && !bestVariant(ci, r.value, values) && !relaxedVariant(ci, r.value, values);
      });
    }

    // Hide option values that are sold out in EVERY combination (true "zombie"
    // values), so dead sizes/colours/packs don't appear in the picker. A value
    // that's only unavailable in the CURRENT selection stays visible (greyed)
    // so the shopper can still pivot to a colour/pack that does carry it.
    function hideDeadOptionValues() {
      const liveByIndex = []; // per option index: Set of values on ≥1 available variant
      product.variants.forEach((v) => {
        if (!v.available) return;
        (v.options || []).forEach((opt, i) => {
          (liveByIndex[i] || (liveByIndex[i] = new Set())).add(normOpt(opt));
        });
      });
      root.querySelectorAll('input[type="radio"][data-af-option-index]').forEach((r) => {
        const idx = parseInt(r.dataset.afOptionIndex, 10) - 1;
        const live = liveByIndex[idx];
        if (live && live.has(normOpt(r.value))) return; // available somewhere → keep
        const tile = r.closest('.af-finish-opt, .af-size-tile');
        if (tile) {
          tile.classList.add('af-tile-dead');
          tile.style.display = 'none';
        }
      });
    }

    // Variant-aware "Product details" spec bullets. Per-variant specs come from a
    // JSON island ([data-af-spec-data]) keyed by variant id; on each variant change
    // rewrite the size / finish / mat bullet text from the selected variant.
    const afSpecData = (function () {
      const el = root.querySelector('[data-af-spec-data]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    })();
    const hudsonGallery = window.AFHudsonGallery ? window.AFHudsonGallery.init(root, product, afSpecData) : null;

    function updateSpec(variant) {
      if (!afSpecData || !variant) return;
      const d = afSpecData[variant.id] || afSpecData[String(variant.id)];
      if (!d) return;
      const sizeEl = root.querySelector('[data-af-spec-size]');
      const sizeLi = root.querySelector('[data-af-spec-size-li]');
      const contentsEl = root.querySelector('[data-af-spec-contents]');
      const contentsLi = root.querySelector('[data-af-spec-contents-li]');
      const finishEl = root.querySelector('[data-af-spec-finish]');
      const matEl = root.querySelector('[data-af-spec-mat]');
      const matLi = root.querySelector('[data-af-spec-mat-li]');
      const buildEl = root.querySelector('[data-af-spec-build]');
      const buildLi = root.querySelector('[data-af-spec-build-li]');
      const dimsEl = root.querySelector('[data-af-spec-dims]');
      const dimsLi = root.querySelector('[data-af-spec-dims-li]');
      const openEl = root.querySelector('[data-af-spec-openings]');
      const openLi = root.querySelector('[data-af-spec-openings-li]');
      const constrEl = root.querySelector('[data-af-spec-constr]');
      const constrLi = root.querySelector('[data-af-spec-constr-li]');
      const dealPill = root.querySelector('[data-af-deal-pill]');
      if (sizeEl && d.size) sizeEl.textContent = d.size;
      // What's in the box (afl.contents). A set's Size option names only one of the
      // frames inside, so when a contents line exists it replaces the size bullet —
      // and comes back to the size bullet when the shopper picks a single frame.
      if (contentsLi) {
        if (d.contents) { if (contentsEl) contentsEl.textContent = d.contents; contentsLi.hidden = false; if (sizeLi) sizeLi.hidden = true; }
        else { contentsLi.hidden = true; if (sizeLi) sizeLi.hidden = false; }
      }
      // Outer dimensions differ per variant and aren't measured for every SKU —
      // hide the bullet entirely rather than showing "Outer size" with nothing after it.
      if (dimsLi) {
        if (d.dims) { if (dimsEl) dimsEl.textContent = d.dims; dimsLi.hidden = false; }
        else dimsLi.hidden = true;
      }
      // Opening count varies per variant on collage frames and is absent on plain
      // single-photo frames — same hide-when-empty rule as the outer size.
      if (openLi) {
        if (d.openings) { if (openEl) openEl.textContent = d.openings; openLi.hidden = false; }
        else openLi.hidden = true;
      }
      // Glazing + mount method. Pre-composed server-side (see the JSON island in
      // af-main-product.liquid) so the wording rules live in one place. Mount method
      // is genuinely per-variant on mirrors — one arched size stands on an easel and
      // ships with no hangers — so a stale bullet here would be actively wrong.
      if (constrLi) {
        if (d.constr) { if (constrEl) constrEl.textContent = d.constr; constrLi.hidden = false; }
        else constrLi.hidden = true;
      }
      // Per-variant deal badge (afl.deal) — deals are per-size, so the pill
      // must follow the picker or a non-deal size would wear the badge.
      if (dealPill) {
        if (d.deal) { dealPill.textContent = d.deal; dealPill.hidden = false; }
        else dealPill.hidden = true;
      }
      if (finishEl) finishEl.textContent = [d.color, d.material].filter(Boolean).join(' · ');
      // Mat line is pre-composed server-side (Mat option → afl.matting, plus the true
      // mat opening) so an unmatted size hides the bullet instead of keeping the
      // previous size's mat text.
      if (matLi) {
        if (d.mattxt) { if (matEl) matEl.textContent = d.mattxt; matLi.hidden = false; }
        else matLi.hidden = true;
      } else if (matEl) {
        if (d.mat) matEl.textContent = 'Matted to ' + d.mat;
        else if (d.matting && !/^(un|no )/i.test(d.matting)) matEl.textContent = d.matting;
      }
      // Frame width + weight (afl.molding / afl.weight) — per variant, hide when empty.
      if (buildLi) {
        if (d.build) { if (buildEl) buildEl.textContent = d.build; buildLi.hidden = false; }
        else buildLi.hidden = true;
      }
      // "Will it fit?" strip — print size / mat opening / outer size follow the picker.
      // Sets hide the whole strip (their contents line already lists every frame) and
      // collage frames hide the print cell, where a single print size would be wrong.
      const fitWrap = root.querySelector('[data-af-fit]');
      if (fitWrap) {
        const isSet = !!d.contents;
        const showPrint = !isSet && !d.openings && !!d.print;
        const pCell = fitWrap.querySelector('[data-af-fit-print-cell]');
        const pEl = fitWrap.querySelector('[data-af-fit-print]');
        const pSub = fitWrap.querySelector('[data-af-fit-print-sub]');
        if (pCell) {
          pCell.hidden = !showPrint;
          if (showPrint) {
            if (pEl) pEl.textContent = d.print;
            if (pSub) {
              pSub.textContent = d.fitst === 'matted' && d.frame ? 'or ' + d.frame + ' with the mat out'
                : (d.fitst === 'unmatted' ? 'edge to edge, no mat' : '');
            }
          }
        }
        const moCell = fitWrap.querySelector('[data-af-fit-mo-cell]');
        const moEl = fitWrap.querySelector('[data-af-fit-mo]');
        if (moCell) { moCell.hidden = isSet || !d.mo; if (d.mo && moEl) moEl.textContent = d.mo; }
        const dCell = fitWrap.querySelector('[data-af-fit-dims-cell]');
        const dEl = fitWrap.querySelector('[data-af-fit-dims]');
        if (dCell) { dCell.hidden = isSet || !d.dims; if (d.dims && dEl) dEl.textContent = d.dims; }
        fitWrap.hidden = isSet || !(showPrint || d.mo || d.dims);
      }
    }

    // Reorder the size grid: keep the collapsed best-sellers (non-extra tiles)
    // in their curated order first, then sort the remaining "Show all" tiles
    // ascending by parsed W x H (width, then height). Values with no parseable
    // dimensions sort to the end.
    function sortExtraSizesAscending() {
      const parseDims = (tile) => {
        const txt = (tile.querySelector('.dim') || {}).textContent || '';
        const m = txt.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
        return m ? [parseFloat(m[1]), parseFloat(m[2])] : [Infinity, Infinity];
      };
      root.querySelectorAll('[data-af-size-grid]').forEach((grid) => {
        const tiles = Array.from(grid.querySelectorAll('.af-size-tile'));
        const collapsed = tiles.filter((t) => !t.classList.contains('af-size-tile--extra'));
        const extra = tiles.filter((t) => t.classList.contains('af-size-tile--extra'));
        extra.sort((a, b) => {
          const A = parseDims(a); const B = parseDims(b);
          return (A[0] - B[0]) || (A[1] - B[1]);
        });
        collapsed.concat(extra).forEach((t) => grid.appendChild(t));
      });
    }

    // Bundle & save (CRO §02.3): annotate each Pack tile with the per-unit price
    // and "Save X%" vs the smallest pack, for the currently selected other
    // options. Purely surfaces the discount already priced into multi-pack
    // variants — no new discounting. The best-saving pack gets a "Best value"
    // flag rendered via CSS on .is-best.
    function updatePackTiles(cur) {
      const list = root.querySelector('[data-af-pack-list]');
      if (!list) return;
      const pi = parseInt(list.dataset.afPackIndex, 10) - 1;
      const packQty = (val) => {
        const m = String(val).match(/\d+/);
        return m ? parseInt(m[0], 10) : 1;
      };
      const tiles = Array.from(list.querySelectorAll('[data-af-pack-tile]'));
      const clearEls = ['[data-af-pack-unit]','[data-af-pack-save]','[data-af-pack-was]','[data-af-pack-price]','[data-af-pack-tag]'];
      const info = [];
      tiles.forEach((tile) => {
        tile.classList.remove('is-best');
        const val = tile.dataset.afPackValue;
        const qtyEl = tile.querySelector('[data-af-pack-qty]');
        if (qtyEl) qtyEl.textContent = packQty(val) + '×';
        const v = bestVariant(pi, val, cur);
        if (v) {
          info.push({ tile, qty: packQty(val), per: v.price / packQty(val), price: v.price });
        } else {
          // No variant carries this pack for the current earlier options — clear
          // any annotations left over from the previous selection (stale price).
          clearEls.forEach((sel) => { const el = tile.querySelector(sel); if (el) { el.hidden = true; el.textContent = ''; } });
        }
      });
      if (!info.length) return;
      const base = info.reduce((a, b) => (b.qty < a.qty ? b : a));
      let best = null;
      info.forEach((i) => {
        const priceEl = i.tile.querySelector('[data-af-pack-price]');
        const unitEl = i.tile.querySelector('[data-af-pack-unit]');
        const wasEl = i.tile.querySelector('[data-af-pack-was]');
        const saveEl = i.tile.querySelector('[data-af-pack-save]');
        const tagEl = i.tile.querySelector('[data-af-pack-tag]');
        const save = base.per > 0 ? Math.round((1 - i.per / base.per) * 100) : 0;
        const show = i.qty > 1;
        const showSave = show && save >= 5;
        // Per-tier total (always) — the multi-pack variant price, savings already priced in.
        if (priceEl) { priceEl.textContent = moneyFmt(i.price); priceEl.hidden = false; }
        // Struck "what N singles would cost" = smallest-pack per-unit x this qty.
        if (wasEl) { wasEl.textContent = showSave ? moneyFmt(Math.round(base.per * i.qty)) : ''; wasEl.hidden = !showSave; }
        if (unitEl) { unitEl.textContent = show ? moneyFmt(Math.round(i.per)) + ' each' : ''; unitEl.hidden = !show; }
        if (saveEl) { saveEl.textContent = showSave ? 'Save ' + save + '%' : ''; saveEl.hidden = !showSave; }
        if (tagEl) { tagEl.hidden = true; tagEl.textContent = ''; }
        if (showSave && (!best || save > best.save)) best = { tile: i.tile, save };
      });
      if (best) {
        best.tile.classList.add('is-best');
        const t = best.tile.querySelector('[data-af-pack-tag]');
        if (t) { t.textContent = 'Best value'; t.hidden = false; }
      }
    }

    // Buy-more-save-more quantity tiers (frames without a Pack option). Prices are the
    // current variant unit price × qty × (1 − tier%), matching the 2/4/6 → 10/15/20%
    // automatic discounts applied at checkout. Recomputed whenever the variant changes.
    function updateBulkTiles(variant) {
      const list = root.querySelector('[data-af-bulk-list]');
      if (!list || !variant) return;
      const unit = variant.price;
      list.querySelectorAll('[data-af-bulk-tile]').forEach((tile) => {
        const qty = parseInt(tile.dataset.afBulkQty, 10);
        const pct = parseInt(tile.dataset.afBulkPct, 10);
        const gross = unit * qty;
        const net = bulkLineTotal(unit, qty, pct);
        const show = pct > 0;
        const set = (sel, txt, hide) => {
          const el = tile.querySelector(sel);
          if (el) { el.textContent = txt; if (hide !== undefined) el.hidden = hide; }
        };
        set('[data-af-bulk-price]', moneyFmt(net), false);
        set('[data-af-bulk-was]', show ? moneyFmt(gross) : '', !show);
        set('[data-af-bulk-unit]', show ? moneyFmt(bulkNetUnit(unit, pct)) + ' each' : '', !show);
        set('[data-af-bulk-save]', show ? 'Save ' + pct + '%' : '', !show);
        set('[data-af-bulk-tag]', qty === 6 ? 'Best value' : '', qty !== 6);
      });
    }

    function updateUI(opts) {
      const initial = opts && opts.initial;
      const values = selectedValues();
      const variant = findVariant(values);
      renderedVariant = variant;

      updateOptionStates(values);
      updatePackTiles(values);

      root.querySelectorAll('.af-finish-opt, .af-size-tile').forEach((el) => {
        const input = el.querySelector('input[type="radio"]');
        if (input) el.classList.toggle('active', input.checked);
      });
      // Mirror the chosen swatch's name beside the COLOR legend (shown on mobile).
      root.querySelectorAll('.af-finish-list--swatch').forEach((list) => {
        const checked = list.querySelector('input[type="radio"]:checked');
        const sec = list.closest('.af-pdp-section');
        const head = sec && sec.querySelector('[data-af-finish-selected]');
        if (head) head.textContent = checked ? checked.value : '';
      });
      updateSizeVisual(values);

      if (!variant) {
        updateOversize(null);
        if (atcBtn) atcBtn.disabled = true;
        if (atcLabel) atcLabel.textContent = 'Unavailable';
        if (stickyBtn) stickyBtn.disabled = true;
        if (stickyLabel) stickyLabel.textContent = 'Unavailable';
        return;
      }
      // Keep the submitted variant id in lockstep with the current selection.
      if (variantIdInput) variantIdInput.value = variant.id;

      // Refresh the Product-details spec bullets for the selected variant.
      updateSpec(variant);
      updateOversize(variant);

      // Don't yank the gallery on first paint — only swap media on user changes.
      if (hudsonGallery) hudsonGallery.update(variant);
      else if (!initial) updateVariantMedia(variant);

      if (priceEl) {
        priceEl.innerHTML = moneyFmt(variant.price);
      }
      updateBulkTiles(variant);
      if (stickyPriceEl) stickyPriceEl.textContent = moneyFmt(variant.price);

      const buyable = variant.available && !forceSoldOut;
      if (atcBtn) atcBtn.disabled = !buyable;
      if (atcLabel) atcLabel.textContent = buyable ? atcLabelText : 'Sold out';
      if (stickyBtn) stickyBtn.disabled = !buyable;
      if (stickyLabel) stickyLabel.textContent = buyable ? atcLabelText : 'Sold out';

      if (!initial && !(opts && opts.restore)) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    root.querySelectorAll('input[type="radio"][data-af-option-index]').forEach((r) => {
      r.addEventListener('change', () => {
        // Snap-forward (Juan, 2026-07-13): picking a value resolves the LATER
        // options to an available variant — e.g. choosing 18x24 moves the mat/
        // colour/pack to a combo that actually exists, instead of leaving the
        // old selection struck and making the shopper guess. EARLIER options
        // are never rewritten (resolveOnChange holds them fixed) once TOUCHED,
        // preserving the no-auto-snap fix for deliberate choices; untouched
        // page-load defaults may float to an available combo (relaxedVariant).
        // updateOptionStates() inside updateUI() then re-strikes.
        const ci = parseInt(r.dataset.afOptionIndex, 10) - 1;
        resolveOnChange(ci, r.value);
        touched.add(ci);          // this option is now a deliberate choice…
        preferred[ci] = r.value;  // …and THIS value is what the shopper wants
        updateUI();
        // Changing any variant option resets the bulk choice: the previous
        // quantity (e.g. another size's full case) doesn't carry over.
        const qtyEl = root.querySelector('[data-af-qty-input]');
        if (qtyEl) qtyEl.value = '1';
        buildBulkOptions();
        recalcCta();
      });
    });

    // LEVEL 1: remove option values with no buyable variant anywhere (dead
    // everywhere) before first paint — a finish/size only shows if it's in
    // stock on at least one current variant.
    hideDeadOptionValues();

    // Non-best-seller sizes render ascending (smallest → largest); the collapsed
    // best-sellers keep their curated order up front.
    sortExtraSizesAscending();

    // Sync the hidden variant id / price / availability to the pre-checked
    // options on load. Previously this ran only on change, so the default
    // variant id could be submitted even after the shopper picked a size.
    updateUI({ initial: true });

    // Deep links to a SOLD-OUT variant (?variant= from ads, feeds, old shares).
    //
    // Until Sep 2026 these were re-pointed to the nearest available variant.
    // That rescues a shopper who clicked a stale link, but it silently changes
    // the PRICE under them — and it is why Google Merchant Center disapproved
    // 10 live variants for "Mismatched product price" (ROI-Works audit, Sep
    // 2026): Google crawls ?variant=<sold out> at its feed price and the page
    // answers with a different variant at a different price.
    //
    // Now: an EXPLICIT ?variant= is honoured even when sold out, so the price
    // and the "Sold out" state match what the feed claims, and we offer the
    // nearest in-stock options instead of switching under the shopper. A dead
    // selection with NO explicit variant (stale/cached HTML) is still healed.
    // The matching server-side rule is in af-main-product.liquid.
    (function handleDeadLanding() {
      const urlVid = new URLSearchParams(window.location.search).get('variant');
      const asked = urlVid && product.variants.find((v) => String(v.id) === urlVid);
      const current = findVariant(selectedValues());
      const avail = product.variants.filter((v) => v.available);
      if (!avail.length) return; // truly sold out — "Sold out" is the truth

      // Available variants ranked by closeness to ref (earlier options weigh more).
      function nearest(ref, n) {
        const score = (v) => {
          let s = 0;
          for (let i = 0; i < v.options.length; i++) {
            if (normOpt(v.options[i]) === normOpt(ref.options[i])) s += v.options.length - i;
          }
          return s;
        };
        return avail.slice().sort((a, b) => score(b) - score(a)).slice(0, n);
      }
      function selectVariant(v) {
        root.querySelectorAll('input[type="radio"][data-af-option-index]').forEach((r) => {
          const i = parseInt(r.dataset.afOptionIndex, 10) - 1;
          if (normOpt(r.value) === normOpt(v.options[i])) r.checked = true;
        });
        updateUI();
      }

      if (asked && !asked.available) {
        renderSoldOutAlternatives(asked, nearest(asked, 4), selectVariant);
        return;
      }
      if (current && !current.available) selectVariant(nearest(current, 1)[0]);
    })();

    // "<option> is sold out — available now:" switcher, rendered under the buy
    // button when an explicit deep link lands on a sold-out variant. Replaces
    // the old silent swap: the shopper still gets one tap to a size we can
    // actually ship, but the price they were shown never changes behind them.
    function renderSoldOutAlternatives(asked, alts, onPick) {
      if (!alts.length || root.querySelector('[data-af-oos-alts]')) return;
      const anchor = root.querySelector('[data-af-atc]');
      if (!anchor) return;
      const box = document.createElement('div');
      box.className = 'af-oos-alts';
      box.setAttribute('data-af-oos-alts', '');
      const label = document.createElement('p');
      label.className = 'af-oos-alts__label';
      label.textContent = (asked.title || 'This option') + ' is sold out. Available now:';
      box.appendChild(label);
      const list = document.createElement('div');
      list.className = 'af-oos-alts__list';
      alts.forEach((v) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'af-oos-alts__btn';
        const name = document.createElement('span');
        name.className = 'af-oos-alts__name';
        name.textContent = v.title;
        const price = document.createElement('span');
        price.className = 'af-oos-alts__price';
        price.textContent = moneyFmt(v.price);
        b.appendChild(name);
        b.appendChild(price);
        b.addEventListener('click', () => {
          onPick(v);
          box.remove();
        });
        list.appendChild(b);
      });
      box.appendChild(list);
      anchor.insertAdjacentElement('afterend', box);
    }

    // Size "Show all sizes" expander — collapses extra tiles behind a toggle button.
    root.querySelectorAll('[data-af-size-toggle]').forEach((btn) => {
      const picker = btn.closest('.af-size-picker');
      const grid = picker && picker.querySelector('[data-af-size-grid]');
      if (!grid) return;
      const moreLabel = btn.querySelector('[data-af-size-more-label]');
      const total = grid.querySelectorAll('.af-size-tile:not(.af-tile-dead)').length;
      function syncLabel() {
        const collapsed = grid.classList.contains('is-collapsed');
        btn.setAttribute('aria-expanded', String(!collapsed));
        if (moreLabel) {
          moreLabel.textContent = collapsed ? 'Show all ' + total + ' sizes' : 'Show fewer sizes';
        }
      }
      btn.addEventListener('click', () => {
        grid.classList.toggle('is-collapsed');
        syncLabel();
      });
      // Always start collapsed on page load (no persisted/auto-expanded state).
      syncLabel();
    });

    const qtyInput = root.querySelector('[data-af-qty-input]');
    const bulkBtns = root.querySelector('[data-af-bulk-btns]');
    const bulkNote = root.querySelector('[data-af-bulk-note]');
    // Per-product value override (afl.bulk_offer = "fixed:<unit rung cents>:<full case cents>"),
    // read from the bulk block so the buttons, the note and the Add-to-cart total quote the
    // flat dollar-off the Function applies instead of a percentage. Null = percentage.
    const bulkDrop = root.querySelector('[data-af-bulk-drop]');
    // A ZERO in either slot means "use that rung's percentage instead of a flat amount", which
    // is how the kids frame runs "fixed:100:0" — $1 off each at its own rung so the .99 ending
    // survives, and a straight 15% on a full case (a flat amount could not be 15% there, since
    // its colours are not all the same price). Mirrors fixedOffer() in the Function.
    const bulkOffer = (function () {
      const m = /^fixed:(?:(\d+):)?(\d+)$/.exec(((bulkDrop && bulkDrop.dataset.afBulkOffer) || '').trim());
      if (!m) return null;
      const full = parseInt(m[2], 10) || 0;
      const unit = m[1] === undefined ? 0 : (parseInt(m[1], 10) || 0);
      return (unit > 0 || full > 0) ? { unit: unit, full: full } : null;
    })();
    // Mirrors CASE_PCT / UNIT_PCT / UNIT_MIN in the Function's
    // cart_lines_discounts_generate_run.js — change them together, or the PDP quotes a saving
    // checkout will not give. UNIT_PCT and UNIT_MIN are ALSO published as a Google Merchant
    // Center promotion ("5% off, minimum 3 items"), so changing either means changing that too.
    const LADDER_CASE_PCT = 15;
    const LADDER_UNIT_PCT = 5;
    const LADDER_UNIT_MIN = 3;
    // afl.bulk_rung lets a PRODUCT override the threshold; otherwise the global one applies.
    const bulkRung = (function () {
      const n = parseInt(((bulkDrop && bulkDrop.dataset.afBulkRung) || '').trim(), 10);
      return n >= 2 ? n : LADDER_UNIT_MIN;
    })();
    function offerUnitDisc(unit, isCase) {
      const flat = bulkOffer ? (isCase ? bulkOffer.full : bulkOffer.unit) : 0;
      if (flat > 0) return flat;
      return bulkUnitDiscount(unit, isCase ? LADDER_CASE_PCT : LADDER_UNIT_PCT);
    }
    function offerNetUnit(unit, isCase) { return unit - offerUnitDisc(unit, isCase); }
    function offerSaveLabel(isCase) {
      const flat = bulkOffer ? (isCase ? bulkOffer.full : bulkOffer.unit) : 0;
      if (flat > 0) return moneyFmt(flat).replace(/\.00$/, '') + ' off each';
      return 'Save ' + (isCase ? LADDER_CASE_PCT : LADDER_UNIT_PCT) + '%';
    }
    // Whole cases inside q, and the remainder. Mirrors splitCase in the Function: only WHOLE
    // cartons earn the rate, so 13 against a case of 12 is 12 discounted and 1 at full price.
    function splitCaseUnits(q, cp) {
      if (!(cp >= 3) || q < cp) return { caseUnits: 0, looseUnits: q };
      const caseUnits = Math.floor(q / cp) * cp;
      return { caseUnits: caseUnits, looseUnits: q - caseUnits };
    }
    // What q units actually cost, split exactly the way the Function splits them. This is the
    // number on the Add-to-cart button, so it has to agree with checkout.
    function ladderTotal(unit, q, cp) {
      const s = splitCaseUnits(q, cp);
      const loosePaid = q >= bulkRung ? offerNetUnit(unit, false) : unit;
      return s.caseUnits * offerNetUnit(unit, true) + s.looseUnits * loosePaid;
    }

    // Bulk-option quick buttons: Single · [product rung] · Full case. Clicking one sets the
    // quantity; the bulk-ladder-discount Shopify Function (gated on the bulk-discount tag)
    // applies the saving at checkout — WHOLE CASES on a line (case pack ≥ 3) → 15% on those
    // units, and 3+ of a PRODUCT → 5% on the rest. That 5%/3 pair is published as a Google
    // Merchant Center promotion, so it cannot move without the promotion moving first.
    // (2026-09-08: 6+ rung removed, full case 15% → 8%. 2026-09-17: full case back to 15% on
    // whole cartons only; the unit rung was briefly dropped and then restored for the promotion.)
    // The discount is derived from the quantity itself, so the Add-to-cart total stays
    // honest even if the shopper nudges the +/- stepper. Keep in step with the Function.
    function casePackFor(v) {
      const d = (v && afSpecData) ? (afSpecData[v.id] || afSpecData[String(v.id)]) : null;
      return (d && d.casepack) ? parseInt(d.casepack, 10) : 0;
    }
    // Frames per case UNIT: a "6 Pack"/"12 Pack" Pack option value means each unit
    // is a multipack, so a full case of N units holds N × pack frames. 1 when the
    // product has no pack option. Mirrors the Liquid initial render (af_pack).
    function packSizeFor(v) {
      if (!v || !Array.isArray(product.options)) return 1;
      const i = product.options.findIndex((n) => /pack/i.test(String(n)));
      const m = i >= 0 ? String(v.options[i] || '').match(/\d+/) : null;
      const n = m ? parseInt(m[0], 10) : 1;
      return n >= 1 ? n : 1;
    }
    // Whether this quantity earns anything at all. The money comes from ladderTotal(),
    // which is the one that understands a part-case line.
    function pctForQty(q, cp) {
      if (cp >= 3 && q >= cp) return LADDER_CASE_PCT; // this line contains a whole case
      if (q >= bulkRung) return LADDER_UNIT_PCT;
      return 0;
    }
    // Rebuild the bulk ladder for the selected variant's case pack. The 3-unit rung
    // shows only while smaller than the case; "Full case (N)" is always the
    // top option when a case size exists (>=3) — a full carton is the logistically
    // clean purchase we want to surface. Mirrors the Liquid initial render.
    function syncBulkActive(q) {
      if (!bulkBtns) return;
      bulkBtns.querySelectorAll('[data-af-bulk-qty]').forEach((b) => {
        const on = parseInt(b.dataset.afBulkQty, 10) === q;
        b.classList.toggle('active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    function buildBulkOptions() {
      if (!bulkBtns) return;
      const v = findVariant(selectedValues());
      const cp = casePackFor(v);
      const pack = packSizeFor(v);
      const btn = (q, isCase) => {
        const earns = isCase || q >= bulkRung;
        const qty = q === 1 ? 'Single' : (isCase ? 'Full Case · ' + q + '<span class="visually-hidden"> units</span>' : q + ' Units');
        const frames = (isCase && pack > 1 && v)
          ? '<span class="frames">' + (q * pack) + ' frames · ' + moneyFmt(Math.round(offerNetUnit(v.price, true) / pack)) + '/frame</span>'
          : '';
        return '<button type="button" class="af-bulk-btn" data-af-bulk-qty="' + q + '"'
          + (isCase ? ' data-af-case' : '') + ' aria-pressed="false">'
          + (isCase ? '<span class="flag">Best value</span>' : '')
          + '<span class="qty">' + qty + '</span>'
          + (earns ? '<span class="save">' + offerSaveLabel(isCase) + '</span>' : '') + frames + '</button>';
      };
      let html = btn(1, false);
      // The unit rung, only while it is smaller than a carton.
      if (!cp || bulkRung < cp) html += btn(bulkRung, false);
      if (cp >= 3) html += btn(cp, true);
      bulkBtns.innerHTML = html;
      // Fresh variant, fresh choice — always reset to "Single" (the qty input is
      // reset alongside by the option-change handler).
      syncBulkActive(1);
    }
    // Sync quantity + Add-to-cart total + savings note to the current bulk selection.
    function recalcCta() {
      const v = findVariant(selectedValues());
      // The quantity input is the single source of truth (driven by BOTH the +/-
      // stepper and the bulk dropdown). Don't re-derive qty from the dropdown here
      // or manual stepper changes to non-breakpoint amounts (2, 4, 8…) get reset.
      const q = Math.max(1, parseInt((qtyInput && qtyInput.value) || '1', 10));
      overBulk = q > BULK_MAX;

      // Over the wholesale threshold: don't attempt a doomed add-to-cart (whose
      // error only flashed unreadably on the button). Show a persistent note and
      // turn the Add button into the wholesale CTA (the submit handler redirects).
      if (overBulk) {
        if (bulkNote) {
          bulkNote.hidden = false;
          bulkNote.classList.add('af-bulk-note--warn');
          bulkNote.innerHTML = 'Orders over ' + BULK_MAX + ' are handled by our wholesale team — <a href="/pages/wholesale">request a wholesale quote</a>.';
        }
        if (atcLabel) atcLabel.textContent = 'Request a wholesale quote';
        if (stickyLabel) stickyLabel.textContent = 'Request a wholesale quote';
        return;
      }

      const cp = casePackFor(v);
      const pct = pctForQty(q, cp);
      const isCase = cp >= 3 && q >= cp;
      if (bulkNote) {
        bulkNote.classList.remove('af-bulk-note--warn');
        if (pct > 0 && v) {
          // Multipacks: q counts case UNITS (12-packs), shoppers think in frames —
          // count q × pack frames and price per frame, not per unit.
          const pack = packSizeFor(v);
          // A part-case line is two rates at once, so quote the true blended per-frame price
          // rather than either rung's own number.
          const perFrame = Math.round(ladderTotal(v.price, q, cp) / q / pack);
          bulkNote.hidden = false;
          bulkNote.innerHTML = (isCase ? 'Full case · ' : '') + (q * pack) + ' frames · <b>' +
            moneyFmt(perFrame) +
            (pack > 1 ? ' per frame</b>' : ' each</b>') + ' · ' + offerSaveLabel(isCase).toLowerCase() + ' (applied at checkout)';
        } else {
          bulkNote.hidden = true; bulkNote.textContent = '';
        }
      }
      if (atcLabel && v && v.available) {
        atcLabel.textContent = atcLabelText + ' · ' + moneyFmt(ladderTotal(v.price, q, cp));
      }
      if (stickyLabel && v && v.available) stickyLabel.textContent = atcLabelText;
    }
    if (bulkBtns) {
      // Delegated, so the buttons buildBulkOptions() rebuilds on variant change
      // keep working without rebinding.
      bulkBtns.addEventListener('click', (e) => {
        const b = e.target.closest('[data-af-bulk-qty]');
        if (!b || !bulkBtns.contains(b)) return;
        const q = Math.max(1, parseInt(b.dataset.afBulkQty || '1', 10));
        if (qtyInput) qtyInput.value = q;
        syncBulkActive(q);
        recalcCta();
      });
    }
    root.querySelectorAll('[data-af-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!qtyInput) return;
        const delta = parseInt(btn.dataset.afQty, 10);
        const q = Math.max(1, parseInt(qtyInput.value || '1', 10) + delta);
        qtyInput.value = q;
        // Stepper landing exactly on a rung highlights that button; any other
        // quantity clears the highlight (the note below still shows the tier).
        syncBulkActive(q);
        recalcCta();
      });
    });
    if (qtyInput) qtyInput.addEventListener('change', () => {
      syncBulkActive(Math.max(1, parseInt(qtyInput.value || '1', 10)));
      recalcCta();
    });

    // Initial paint of the bulk option (the consts above are now initialized).
    buildBulkOptions();
    recalcCta();

    function restoreVariantSelection(fromHistory = true) {
      const urlId = new URLSearchParams(window.location.search).get('variant');
      const variant = (fromHistory && product.variants.find((v) => String(v.id) === urlId)) || renderedVariant;
      if (!variant) return;
      root.querySelectorAll('input[type="radio"][data-af-option-index]').forEach((radio) => {
        const i = parseInt(radio.dataset.afOptionIndex, 10) - 1;
        radio.checked = normOpt(radio.value) === normOpt(variant.options[i]);
      });
      const sameVariant = renderedVariant && renderedVariant.id === variant.id;
      updateUI({ restore: true, initial: !!sameVariant });
      buildBulkOptions();
      syncBulkActive(Math.max(1, parseInt((qtyInput && qtyInput.value) || '1', 10)));
      recalcCta();
    }

    // History traversal restores native form state AFTER pageshow/popstate.
    // Run on the next task so size, finish, mat, price and submitted ID agree.
    // pageshow covers both a cached document and a newly loaded Back/Forward
    // destination; popstate also covers history within the same document.
    let restoreTimer;
    function queueVariantRestore() {
      clearTimeout(restoreTimer);
      restoreTimer = setTimeout(restoreVariantSelection, 0);
    }
    window.addEventListener('pageshow', queueVariantRestore);
    window.addEventListener('popstate', queueVariantRestore);

    // Scrollable options section: show the bottom fade only when it overflows, and hide it
    // once scrolled to the end. Recompute on scroll, resize, and any click inside (the size
    // "Show all" expander / tier changes alter the content height).
    const scrollEl = root.querySelector('[data-af-pdp-scroll]');
    const scrollWrap = root.querySelector('[data-af-pdp-scroll-wrap]');
    function updateScrollFade() {
      if (!scrollEl || !scrollWrap) return;
      scrollWrap.classList.toggle('is-scrollable', scrollEl.scrollHeight - scrollEl.clientHeight > 2);
      const atEnd = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 2;
      scrollWrap.classList.toggle('is-scrolled-end', atEnd);
    }
    if (scrollEl) {
      scrollEl.addEventListener('scroll', updateScrollFade, { passive: true });
      scrollEl.addEventListener('click', function () { requestAnimationFrame(updateScrollFade); }, true);
      window.addEventListener('resize', updateScrollFade);
      requestAnimationFrame(updateScrollFade);
    }

    // Thumb clicks are DELEGATED to the strip (not bound per-thumb) so the
    // dynamic slot updateVariantMedia() creates later works without rebinding.
    const mainImageBox = root.querySelector('[data-af-main-image]');
    const thumbStrip = root.querySelector('[data-af-thumbs]');
    if (thumbStrip) {
      thumbStrip.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-af-thumb-index]');
        if (!thumb || !thumbStrip.contains(thumb)) return;
        root.querySelectorAll('[data-af-thumb-index]').forEach((t) => t.classList.remove('active'));
        thumb.classList.add('active');
        // Video thumbs swap the main box to a player instead of an <img>.
        // Removing the player node on the next swap (innerHTML below, or
        // updateVariantMedia on a variant change) is what stops playback.
        const mediaType = thumb.dataset.afThumbMediaType;
        if ((mediaType === 'video' || mediaType === 'external_video') && mainImageBox) {
          mainImageBox.innerHTML = '';
          mainImageBox.classList.remove('is-primary');
          if (mediaType === 'video') {
            const vid = document.createElement('video');
            vid.controls = true;
            vid.playsInline = true;
            vid.preload = 'metadata';
            try {
              JSON.parse(thumb.dataset.afVideoSources || '[]').forEach((s) => {
                const srcEl = document.createElement('source');
                srcEl.src = s.url;
                if (s.mime) srcEl.type = s.mime;
                vid.appendChild(srcEl);
              });
            } catch (err) { /* malformed sources: leave an empty player rather than crash the picker */ }
            mainImageBox.appendChild(vid);
            vid.play().catch(() => {}); // autoplay-with-sound is often blocked; controls remain
          } else {
            const host = thumb.dataset.afVideoHost;
            const extId = thumb.dataset.afVideoId || '';
            const frame = document.createElement('iframe');
            frame.src = host === 'youtube'
              ? 'https://www.youtube.com/embed/' + extId + '?autoplay=1&playsinline=1'
              : 'https://player.vimeo.com/video/' + extId + '?autoplay=1';
            frame.allow = 'autoplay; encrypted-media; picture-in-picture';
            frame.allowFullscreen = true;
            frame.className = 'af-pdp-video-embed';
            frame.title = 'Product video';
            mainImageBox.appendChild(frame);
          }
          return;
        }
        const src = thumb.dataset.afThumbSrc;
        const alt = thumb.dataset.afThumbAlt;
        if (src && mainImageBox) {
          mainImageBox.innerHTML = '<img src="' + src + '" alt="' + (alt || '') + '" loading="eager">';
          // Only the primary (variant) frame shots get the floated 80% +
          // drop-shadow treatment; lifestyle/scene thumbnails stay full-bleed.
          mainImageBox.classList.toggle('is-primary', thumb.dataset.afThumbPrimary === 'true');
        }
      });
    }
  }

  function bootstrap() {
    document.querySelectorAll('[data-af-product-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();

/* Delivery estimate + order-cutoff countdown.
   Processing = business days (Mon–Fri; weekends skipped, holidays NOT).
   Transit  = business days added after the ship date (weekends skipped).
   If the order is placed before the cutoff hour (US Eastern) on a business
   day, processing starts that day; otherwise it starts the next business day.
   The "Order within" line counts down to that processing day's cutoff. */
(function () {
  const el = document.querySelector('[data-af-delivery]');
  if (!el) return;
  const dateEl = el.querySelector('[data-af-delivery-date]');
  const cdEl = el.querySelector('[data-af-delivery-countdown]');
  const processingBizDays = parseInt(el.dataset.processingDays, 10) || 0;
  const transitBizDays = parseInt(el.dataset.shippingDays, 10) || 0;
  const cutoffHour = parseInt(el.dataset.cutoffHour, 10);
  const hasCutoff = !isNaN(cutoffHour);
  const TZ = 'America/New_York';

  // "Now" expressed in Eastern time, as a Date we can do local arithmetic on.
  function easternNow() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: TZ }));
  }
  function isWeekend(d) { const g = d.getDay(); return g === 0 || g === 6; }
  function nextBusinessDay(d) {
    const n = new Date(d);
    do { n.setDate(n.getDate() + 1); } while (isWeekend(n));
    return n;
  }
  function addBusinessDays(d, n) {
    const r = new Date(d);
    let left = n;
    while (left > 0) { r.setDate(r.getDate() + 1); if (!isWeekend(r)) left--; }
    return r;
  }
  function fmtDate(d) {
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  function render() {
    const now = easternNow();
    const today = new Date(now); today.setHours(0, 0, 0, 0);
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const cutoffMin = hasCutoff ? cutoffHour * 60 : -1;

    // First business day processing begins on.
    let processingStart;
    if (hasCutoff && !isWeekend(today) && nowMin < cutoffMin) {
      processingStart = today;
    } else {
      processingStart = nextBusinessDay(today);
    }

    // Ship after N business days of processing, then N business days of transit.
    const shipDate = addBusinessDays(processingStart, processingBizDays);
    const deliver = addBusinessDays(shipDate, transitBizDays);
    if (dateEl) dateEl.textContent = 'Delivery by ' + fmtDate(deliver);

    if (cdEl && hasCutoff) {
      // Count down to the cutoff on the processing-start day.
      const target = new Date(processingStart); target.setHours(cutoffHour, 0, 0, 0);
      const totalMin = Math.max(0, Math.floor((target - now) / 60000));
      const days = Math.floor(totalMin / 1440);
      const hrs = Math.floor((totalMin % 1440) / 60);
      const mins = totalMin % 60;
      if (days > 0) cdEl.textContent = 'Order within ' + days + 'd ' + hrs + 'h';
      else if (hrs > 0) cdEl.textContent = 'Order within ' + hrs + ' hr ' + mins + ' min';
      else cdEl.textContent = 'Order within ' + mins + (mins === 1 ? ' min' : ' mins');
    } else if (cdEl) {
      cdEl.textContent = '';
    }
  }

  render();
  setInterval(render, 30000);
})();

/* Mobile sticky Add-to-cart bar — slides up once the main Add-to-cart row has
   scrolled ABOVE the viewport (i.e. the shopper scrolled past it), and hides
   again when it returns into view. CSS keeps the bar display:none on desktop.
   Driven by a rAF-throttled scroll listener, NOT IntersectionObserver: IO only
   reports intersection *changes*, so an instant in-page jump that never passes
   the CTA through the viewport (tapping the "reviews" anchor link, browser
   scroll restoration on back/refresh) goes below→above with no transition and
   the bar silently never appears. */
(function () {
  const bar = document.querySelector('[data-af-sticky-bar]');
  const cta = document.querySelector('.af-pdp-cta');
  if (!bar || !cta) return;
  // Mark the page so CSS can reserve footer clearance for the fixed sticky bar
  // (prevents the bar from cropping the footer payment icons at page bottom).
  document.body.classList.add('af-has-sticky-atc');
  let ticking = false;
  const update = () => {
    ticking = false;
    const scrolledPast = cta.getBoundingClientRect().bottom < 0;
    bar.classList.toggle('is-visible', scrolledPast);
    bar.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
    // Lift the Klaviyo minimized teaser above the sticky bar so it doesn't
    // cover the Add-to-cart button (see .af-atc-visible rule in CSS).
    document.body.classList.toggle('af-atc-visible', scrolledPast);
  };
  const onScroll = () => {
    if (!ticking) { ticking = true; requestAnimationFrame(update); }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
})();
